import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ComponentRendering, Text } from '@sitecore-jss/sitecore-jss-react';
import { isEmptyResponse } from '../../../utils/checkEmptyResponse';
import { WithTranslation } from 'react-i18next';
import { TNavigationBarProps, TTabsNavigationBar } from './types/navigationBar.types';
import { NavigationBarModel } from './models/navigationBar.model';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { documentService, windowService } from '../../../utils/window.utils';
import { ANCHOR_COMPONENT, NAVIGATION_BAR_ANCHOR_CLASS } from '../../../constants/navigationBar.constants';
import breakpoints from '../../../constants/breakpoints.constants';
import NavigationTabsMobile from './components/navigationTabsMobile';
import NavigationBarDropdownDesktop from './components/navigationBarDropdownDesktop';
import NavigationBarTabsDesktop from './components/navigationBarTabsDesktop';
import NavigationBarMSRPDesktop from './components/navigationBarMSRPDesktop';
import NavigationBarCtaDesktop from './components/navigationBarCtaDesktop';
import './styles.scss';
import { disableScroll, enableScroll, isScrolling } from '../../../utils/scroll.utils';

/** to be changed along with margin-top value in .product-white-spacer class in 'src/styles.scss' */
const marginTop = 32;

const hiddenTabsBasedOnContentAvailability = (tabs, componentsJssMain, anchorComponents, isEditorial): TTabsNavigationBar[] => {
  return tabs?.map((tab, index) => {
    const tempTab = { ...tab };

    for (let i = 0; i < componentsJssMain.length; i++) {
      // try to find a ProductSeriesNavigationBarAnchorComponent
      if (componentsJssMain[i].componentName === ANCHOR_COMPONENT) {
        // if in the anchorComponent with index === current tab there is an uid === componentsJssMain.uid that means im in the right position
        if (anchorComponents[index].uid === componentsJssMain[i].uid) {
          // if i find one anchor component and then the next is another anchor component it means that i dont have any components to show between the anchor components so the tab must be hidden
          if (componentsJssMain[i].componentName === ANCHOR_COMPONENT && componentsJssMain[i + 1]?.componentName === ANCHOR_COMPONENT) {
            tempTab.isVisible = false;
            break;
          } else {
            // find next anchor component
            const indexNextAnchorComponents = componentsJssMain.findIndex(
              (component) => component.uid === anchorComponents[index + 1]?.uid
            );
            // if there are no others anchor component, then check the components until the end of length for componentJssMain
            const checknComponentsBetween = isEditorial
              ? indexNextAnchorComponents > index && indexNextAnchorComponents !== -1
              : indexNextAnchorComponents !== -1;
            const nComponentsBetween = checknComponentsBetween ? indexNextAnchorComponents - i - 1 : componentsJssMain.length - i - 1;
            tempTab.isVisible = false;
            for (let j = i + 1; j <= i + nComponentsBetween; j++) {
              // if true there is at least one component to show, and so exit from the loop
              const checkComponent = isEditorial ? componentsJssMain[j].componentName : isEmptyResponse(componentsJssMain[j]);
              if (checkComponent) {
                tempTab.isVisible = true;
                break;
              }
            }
            if (tempTab.isVisible) break;
          }
        }
      }
    }
    return tempTab;
  });
};
const NavigationSection: FC<WithTranslation & TNavigationBarProps> = (props) => {
  const { sticky, isEditorial, dropdownIsOpen, setshowPanel } = props;
  const model = useMemo(() => new NavigationBarModel(props), [props]);
  const componentsJssMain = props?.sitecoreContext?.route?.placeholders['jss-main'];
  const isMobile = useMediaQuery(`(max-width: ${breakpoints['tbl-l']}px)`);
  const showDropdown = model?.showDropdown;
  const showLabel = model?.showLabel;
  const dropDownSeriesData = model?.seriesList;
  const [tabs, setTabs] = useState<TTabsNavigationBar[] | null>(null);
  /* eslint-disable-next-line */
  const [anchorComponents, setAnchorComponents] = useState<any>();
  const [activeTab, setActiveTab] = useState<number>(0);

  const handleClick = useCallback((): void => {
    dropDownSeriesData && dropDownSeriesData?.length > 1 && setshowPanel(!dropdownIsOpen);
  }, [setshowPanel, dropdownIsOpen]);

  const handleDropDown = useCallback((): void => {
    setshowPanel(false);
  }, [setshowPanel]);

  const scrollToPlaceholder = (index): void => {
    if (isScrolling()) return;

    const offsetCorrection = isMobile ? 50 : 74;
    const topOffset = anchorComponents?.item(index)?.offsetTop - offsetCorrection;

    const navigationElement = anchorComponents?.item(index)?.nextSibling;
    const hasWhiteSpaceSelector = !!(navigationElement && navigationElement?.querySelector('.product-white-spacer'));
    const marginTopOffest = hasWhiteSpaceSelector ? marginTop : 0;

    if (topOffset) {
      disableScroll();
      windowService().scroll({
        top: topOffset + marginTopOffest,
        behavior: 'smooth',
      });
    }
    enableScroll();
  };

  const detectActiveTab = useCallback(() => {
    if (!anchorComponents) return;
    const sectionHighlightTabHeight = windowService().innerHeight / 4;

    // if at least the first element is not in the highlighted section set the active tab to first
    if (anchorComponents.item(0)?.getBoundingClientRect()?.top > sectionHighlightTabHeight) {
      setActiveTab(0);
      return;
    }

    let currentActiveTab = 0;
    for (let i = 0; i < anchorComponents.length; i++) {
      if (anchorComponents?.item(i)?.getBoundingClientRect().top < sectionHighlightTabHeight) {
        currentActiveTab = i;
      } else {
        break;
      }
    }
    setActiveTab(currentActiveTab);
  }, [anchorComponents]);

  // detect active tab
  useEffect(() => {
    try {
      windowService().addEventListener('scroll', detectActiveTab, { passive: true });
    } catch (e) {
      windowService().addEventListener('scroll', detectActiveTab);
    }
    return (): void => {
      windowService().removeEventListener('scroll', detectActiveTab);
    };
  }, [anchorComponents, detectActiveTab]);

  // show only the tab for the anchor component in page -> 3 anchor component = 3 tab
  useEffect(() => {
    if (model?.tabs?.length === 0) return;
    const anchorComponentsObjList = (componentsJssMain || []).filter((component: ComponentRendering) => component?.componentName === ANCHOR_COMPONENT);
    if (anchorComponentsObjList) {
      const tabsOfAnchorComp = model?.tabs
        .filter((_, index) => index < anchorComponentsObjList.length)
        .map((item) => {
          return {
            label: item,
            isVisible: true,
          };
        });

      const visibleTabs = hiddenTabsBasedOnContentAvailability(tabsOfAnchorComp, componentsJssMain, anchorComponentsObjList, isEditorial);
      setTabs(visibleTabs);
    }
  }, [componentsJssMain, isEditorial, model]);
  //retrieve the anchor components
  useEffect(() => {
    if (model?.tabs?.length === 0) return;
    if (!tabs) return;
    const components = documentService().querySelectorAll(`.${NAVIGATION_BAR_ANCHOR_CLASS}`);
    setAnchorComponents(components);
  }, [model, tabs]);

  useEffect(() => {
    const arr = documentService().getElementsByTagName('header');
    if (arr?.length) {
      const header = arr[0];
      const height = '85px';
      header.style.setProperty('--navigation--Height--actual', height);
    }
  }, [isMobile]);

  useEffect(() => {
    const handleClose = () => {
      setshowPanel(false);
    };

    if (dropdownIsOpen) {
      documentService()?.addEventListener('click', handleClose);
    }

    return () => {
      documentService()?.removeEventListener('click', handleClose);
    };
  }, [dropdownIsOpen]);

  if (isMobile) {
    return (
      <>
        <NavigationTabsMobile
          tabs={tabs}
          sticky={sticky}
          activeTab={activeTab}
          isEditorial={isEditorial}
          scrollToPlaceholder={scrollToPlaceholder}
        />
      </>
    );
  }

  return (
    <div className={`navigation-bar-container ${sticky ? 'sticky-element--hide' : ''}`}>
      {!isEditorial && showDropdown && (
        <NavigationBarDropdownDesktop
          title={model?.title}
          handleClick={handleClick}
          dropDownSeriesData={dropDownSeriesData}
          contextItemUrl={model?.contextItemUrl}
          dropdownIsOpen={dropdownIsOpen}
          handleDropDown={handleDropDown}
        />
      )}
      {!!(isEditorial && model.editorialTitle) && (
        <Text field={model.editorialTitle} tag="h2" className="navigation-bar-container__editorial-title" />
      )}
      <NavigationBarTabsDesktop tabs={tabs} activeTab={activeTab} scrollToPlaceholder={scrollToPlaceholder} isEditorial={isEditorial} />
      {!isEditorial && showLabel && (
        <NavigationBarMSRPDesktop optionalLabel1={model.optionalLabel1} optionalLabel2={model.optionalLabel2} msrp={model.msrp} />
      )}
      <NavigationBarCtaDesktop isEditorial={isEditorial} editorialCTA={model.editorialCTA} button1={model.button1} isMobile={isMobile} enableBuildPriceCTA={model.enableBuildPriceCTA} />
    </div>
  );
};

export default React.memo(NavigationSection);
